import jsPDF from 'jspdf';
import 'jspdf-autotable';

const generateInvoicePdf = (invoice, patient, appConfig, templateId = 'classic') => {
  const doc = new jsPDF();
  const clinicName = appConfig?.centerName || 'Clinique';
  const address = appConfig?.centerAddress || '';
  const phone = appConfig?.centerPhone || '';
  const website = appConfig?.centerWebsite || '';
  const logoUrl = appConfig?.centerLogoUrl || '';
  const pharmacyName = appConfig?.pharmacyName || 'Pharmacie';
  const laboratoryName = appConfig?.laboratoryName || 'Laboratoire';
  const date = new Date(invoice.date).toLocaleDateString();

  const drawHeader = () => {
    if (templateId === 'modern') {
      doc.setFillColor(6, 82, 221);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setFontSize(20);
      doc.setTextColor('#ffffff');
      doc.text(clinicName, 14, 20);
      doc.setFontSize(10);
      doc.text(`${address}`, 14, 28);
      doc.text(`${phone}${phone && website ? ' • ' : ''}${website}`, 14, 34);
      doc.setTextColor('#000000');
    } else if (templateId === 'haiti') {
      doc.setFillColor(220, 36, 29);
      doc.rect(0, 0, 210, 18, 'F');
      doc.setFillColor(255, 204, 0);
      doc.rect(0, 18, 210, 12, 'F');
      doc.setFontSize(18);
      doc.setTextColor('#000000');
      doc.text(clinicName, 14, 16);
      doc.setFontSize(10);
      doc.text(`${address}`, 14, 24);
      doc.text(`Tél: ${phone}${phone && website ? ' • ' : ''}${website}`, 14, 30);
    } else {
      if (logoUrl) {
        try {
          doc.addImage(logoUrl, 'JPEG', 14, 14, 30, 30);
        } catch (e) {
          // ignore invalid logo URLs
        }
      }
      doc.setFontSize(18);
      doc.setTextColor('#333333');
      doc.text(clinicName, logoUrl ? 50 : 14, 24);
      doc.setFontSize(10);
      doc.text(`${address}`, logoUrl ? 50 : 14, 30);
      doc.text(`Tél: ${phone}${phone && website ? ' • ' : ''}${website}`, logoUrl ? 50 : 34);
    }
  };

  drawHeader();
  doc.setFontSize(12);
  doc.setTextColor('#222222');
  doc.text(`Facture #${invoice.id}`, 14, templateId === 'modern' ? 46 : 56);
  doc.text(`Patient : ${patient.name}`, 14, templateId === 'modern' ? 52 : 62);
  doc.text(`Date : ${date}`, 14, templateId === 'modern' ? 58 : 68);
  doc.text(`Statut : ${invoice.status === 'paid' ? 'Payée' : 'Non Payée'}`, 14, templateId === 'modern' ? 64 : 74);
  doc.text(`Total : ${invoice.total.toFixed(2)} ${invoice.currency || 'EUR'}`, 14, templateId === 'modern' ? 70 : 80);

  const tableData = invoice.items.map(item => [
    item.description,
    item.quantity.toString(),
    item.price.toFixed(2),
    (item.quantity * item.price).toFixed(2)
  ]);

  doc.autoTable({
    startY: templateId === 'modern' ? 82 : 92,
    head: [['Description', 'Quantité', 'Prix Unitaire', 'Total']],
    body: tableData,
    styles: { fontSize: 10 },
    headStyles: { fillColor: templateId === 'haiti' ? [220, 36, 29] : [41, 128, 185] }
  });

  const bottomY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 140;
  doc.setFontSize(10);
  doc.text(`Pharmacie : ${pharmacyName}`, 14, bottomY);
  doc.text(`Laboratoire : ${laboratoryName}`, 14, bottomY + 6);
  doc.save(`facture-${invoice.id}.pdf`);
};

export const generatePatientDossierPdf = (patient, labOrders = [], invoices = [], cashTransactions = [], pharmacyStock = [], appConfig = {}) => {
  const doc = new jsPDF();
  const clinicName = appConfig?.centerName || 'Clinique';
  const title = `Dossier patient ${patient.code || patient.name}`;

  const addSection = (titleText, lines) => {
    doc.setFontSize(14);
    doc.text(titleText, 14, doc.lastAutoTable ? doc.lastAutoTable.finalY + 14 : 90);
    doc.setFontSize(10);
    let y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 96;
    lines.forEach(line => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, 14, y);
      y += 6;
    });
  };

  doc.setFontSize(18);
  doc.text(clinicName, 14, 20);
  doc.setFontSize(14);
  doc.text(title, 14, 30);
  doc.setFontSize(10);
  doc.text(`Date de génération : ${new Date().toLocaleDateString()}`, 14, 38);

  const patientLines = [
    `Nom : ${patient.name || '—'}`,
    `Code : ${patient.code || '—'}`,
    `ID : ${patient.id}`,
    `Date inscription : ${patient.dateInscription || '—'}`,
    `Téléphone : ${patient.phone || '—'}`,
    `Adresse : ${patient.adresse || '—'}`
  ];
  addSection('Informations patient', patientLines);

  if (patient.consultations && patient.consultations.length) {
    const body = patient.consultations.map(c => [
      c.date || '—',
      c.praticien || '—',
      (Array.isArray(c.impressionClinique) ? c.impressionClinique.filter(i => i).join(' | ') : c.impressionClinique || '—'),
      c.diagnostic || '—'
    ]);
    doc.autoTable({
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 120,
      head: [['Date', 'Praticien', 'Impression clinique', 'Diagnostic']],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });
  }

  const patientLabOrders = labOrders.filter(o => String(o.patientId) === String(patient.id));
  if (patientLabOrders.length) {
    const body = patientLabOrders.map(o => [
      o.id,
      o.date || '—',
      (o.tests || []).join(', '),
      (o.sentTo || []).join(', '),
      o.status || '—'
    ]);
    doc.autoTable({
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 120,
      head: [['Ordre', 'Date', 'Tests', 'Services', 'Statut']],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });
  }

  const patientInvoices = invoices.filter(inv => String(inv.patientId) === String(patient.id));
  if (patientInvoices.length) {
    const body = patientInvoices.map(inv => [
      inv.id,
      inv.date || '—',
      inv.total?.toFixed(2) || '0.00',
      inv.status || '—'
    ]);
    doc.autoTable({
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 120,
      head: [['Facture', 'Date', 'Montant', 'Statut']],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });
  }

  const patientCashTx = cashTransactions.filter(tx => String(tx.patientId) === String(patient.id));
  if (patientCashTx.length) {
    const body = patientCashTx.map(tx => [
      tx.id,
      tx.date || '—',
      tx.amount?.toFixed(2) || '0.00',
      tx.type || '—',
      tx.invoiceId || '—'
    ]);
    doc.autoTable({
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 120,
      head: [['Transaction', 'Date', 'Montant', 'Type', 'Facture']],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });
  }

  doc.save(`dossier-patient-${patient.code || patient.id}.pdf`);
};

export default generateInvoicePdf;
